/**
 * This source code is quoted from rc-upload.
 * homepage: https://github.com/react-component/upload
 */
import classNames from 'classnames';
import omit from "omit.js"
import React, { useRef, useState, useImperativeHandle, forwardRef, useCallback, useEffect } from 'react';
import defaultRequest from './request.js';
import getUid from './uid.js';
import attrAccept from './attr-accept';
import traverseFileTree from './traverseFileTree';

import { AjaxUploaderProps, DefaultListArr } from './iUpload'
// export const propTypes = {
//     component: PropTypes.string,
//     style: PropTypes.object,
//     clsPrefix: PropTypes.string,
//     className: PropTypes.string,
//     multiple: PropTypes.bool,
//     disabled: PropTypes.bool,
//     accept: PropTypes.string,
//     listType: PropTypes.string,
//     fileList: PropTypes.array,
//     children: PropTypes.any,
//     onStart: PropTypes.func,
//     data: PropTypes.oneOfType([
//         PropTypes.object,
//         PropTypes.func,
//     ]),
//     headers: PropTypes.object,
//     defaultFileList: PropTypes.any,
//     beforeUpload: PropTypes.func,
//     customRequest: PropTypes.func,
//     withCredentials: PropTypes.bool,
//     mergeFiles: PropTypes.bool,
//     supportServerRender: PropTypes.bool,
//     action: PropTypes.string,
//     name: PropTypes.string,
//     onProgress: PropTypes.func,
//     onSuccess: PropTypes.func,
//     onError: PropTypes.func,
//     onChange: PropTypes.func,
//     size: PropTypes.number,
//     showUploadList: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
//     directory: PropTypes.any
// }

const AjaxUploader = forwardRef<any, AjaxUploaderProps>((props, ref) => {
    const {
        component: Tag = 'span', clsPrefix, className, disabled, style, multiple, accept, children, directory,
        ...others
    } = props;
    const [uid, setUid] = useState(getUid());
    const reqsRef = useRef<Record<string, any>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);
    const propsRef = useRef(props); //  post 方法中用到 无法通过props 获取最新的值 因此使用ref来保存最新的props

    useEffect(() => {
        propsRef.current = props;
    });

    useEffect(() => {
        return () => {
            abort();
        };
    }, []);

    const reset = useCallback(() => {
        setUid(getUid());
    }, []);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        uploadFiles(files as any);
        reset();
    };

    const onClick = useCallback(() => {
        const el = fileInputRef.current;
        if (!el) {
            return;
        }
        el.click();
        let cloneInput = el.cloneNode(true);
        document.body.appendChild(cloneInput);
    }, []);

    const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter') {
            onClick();
        }
    }, [onClick]);

    const onFileDrop = useCallback((e: React.DragEvent) => {
        if (e.type === 'dragover') {
            e.preventDefault();
            return;
        }

        if (props.directory) {
            traverseFileTree(Array.prototype.slice.call(e.dataTransfer.items), uploadFiles, function(_file: any) {
                return attrAccept(_file, '*');
            });
        } else {
            const files = e.dataTransfer.files;
            uploadFiles(files as any);
        }
        e.preventDefault();
    }, [props.directory]);

    const uploadFiles = (files: DefaultListArr[]) => {
        const postFiles = Array.prototype.slice.call(files);
        const len = postFiles.length;
        const {mergeFiles} = props;
        if (mergeFiles) {
            const file = {filesArray: postFiles, uid: getUid()} as any;
            upload(file, postFiles);
        } else {
            for (let i = 0; i < len; i++) {
                const file = postFiles[i];
                file.uid = getUid();
                upload(file, postFiles);
            }
        }
    };

    const post = (file: DefaultListArr) => {
        const currentProps = propsRef.current;
        let {data, xsrf, action, onStart} = currentProps;
        if (typeof data === 'function') {
            data = data(file);
        }
        const {uid} = file;
        const request = currentProps.customRequest || defaultRequest;
        let headerData = currentProps.headers;
        let xsrfSt = (window as any)?.tnsSdk?.readXscfToken();
        let headerKeyObj = {};
        if (action) {
            headerKeyObj = (window as any)?.tnsSdk?.generateUriToken?.(action) || {};
        }
        if (xsrf && xsrfSt != null) {
            Object.assign(headerData!, {'X-XSRF-TOKEN': xsrfSt}, headerKeyObj);
        }
        reqsRef.current[uid] = request({
            action: action,
            filename: currentProps.name,
            file: file.filesArray || file,
            data,
            headers: headerData,
            withCredentials: currentProps.withCredentials,
            onProgress: (e: any) => {
                currentProps.onProgress?.(e, file);
            },
            onSuccess: (ret: any) => {
                delete reqsRef.current[uid];
                currentProps.onSuccess?.(ret, file);
            },
            onError: (err: any, ret: any) => {
                delete reqsRef.current[uid];
                currentProps.onError?.(err, ret, file);
            },
        });
        onStart?.(file);
    };


    const upload = (file: DefaultListArr, fileList: DefaultListArr[]) => {
        const currentProps = propsRef.current;
        if (!currentProps.beforeUpload) {
            // always async in case use react state to keep fileList
            return setTimeout(() => post(file), 0);
        }

        const before: any = currentProps.beforeUpload(file, fileList);
        if (before && before.then) {
            before.then((processedFile: any) => {
                const processedFileType = Object.prototype.toString.call(processedFile);
                if (processedFileType === '[object File]' || processedFileType === '[object Blob]') {
                    post(processedFile);
                } else {
                    post(file);
                }
            });
        } else if (before !== false) {
            console.log('beforeUpload return false', currentProps)
            setTimeout(() => post(file), 0);
        }
    };

    const abort = useCallback((file?: any) => {
        const reqs = reqsRef.current;
        if (file) {
            let uid = file;
            if (file && file.uid) {
                uid = file.uid;
            }
            if (reqs[uid] && reqs[uid].abort) {
                reqs[uid].abort();
            }
            delete reqs[uid];
        } else {
            Object.keys(reqs).forEach((uid) => {
                if (reqs[uid] && reqs[uid].abort) {
                    reqs[uid].abort();
                }
                delete reqs[uid];
            });
        }
    }, []);

    useImperativeHandle(ref, () => ({
        abort
    }));
    const cls = classNames({
        [`${clsPrefix}-inner`]: true,
        [`${clsPrefix}-disabled`]: disabled,
        [`${className}`]: className,
    });
    const events = disabled ? {} : {
        onClick: onClick,
        onKeyDown: onKeyDown,
        onDrop: onFileDrop,
        onDragOver: onFileDrop,
        tabIndex: 0 as any,
    };
    return (
        <Tag
            {...events}
            className={cls}
            role="button"
            style={style}
            {...omit(others, ["onRemove", "onPreview", "onDownload", "type", "multipart", "onReady", "onChange", "name",
                "action", "headers", "data", "listType", "fileList", "size", "defaultFileList", "beforeUpload", "showUploadList",
                "supportServerRender", "onStart", "onSuccess", "customRequest", "withCredentials", "mergeFiles", "xsrf"])}
        >
            {
                directory ? (<input
                    type="file"
                    // @ts-ignore webkitdirectory属性校验
                    webkitdirectory='true'
                    ref={fileInputRef}
                    key={uid}
                    style={{display: 'none'}}
                    accept={accept}
                    multiple={multiple}
                    onChange={onChange}
                />) : (<input
                    type="file"
                    ref={fileInputRef}
                    key={uid}
                    style={{display: 'none'}}
                    accept={accept}
                    multiple={multiple}
                    onChange={onChange}
                />)
            }
            {children}
        </Tag>
    );
});

AjaxUploader.displayName = 'AjaxUploader';

export default AjaxUploader;
